import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { PackCard } from '../components/PackCard'
import { SectionHeading, TIER_ACCENT, TIER_LABEL } from '../components/ui'
import { percent, usd } from '../lib/format'
import { MAX_CARDS, MIN_CARDS, TIERS, hitsFor, packId, priceFor, summaryFor } from '../lib/packs'

const LENGTHS = Array.from({ length: MAX_CARDS - MIN_CARDS + 1 }, (_, i) => MIN_CARDS + i)

/**
 * The shop, and — with six products — also the comparison.
 *
 * A grid built for twenty-one packs looked broken holding a handful, so this
 * page stopped being a grid. It is six large tiles over the price ladder they
 * open onto, which is the thing a buyer actually wants to see: what each extra
 * card costs, on each tier, side by side.
 */
export function PacksPage() {
  // Resolved locally: a pack is a tier and a length, and the browser runs the
  // same generator the server prices against.
  const packs = useMemo(() => TIERS.map((tier) => summaryFor({ tier, cards: MIN_CARDS })), [])

  return (
    <div className="py-6">
      <SectionHeading
        title="Packs"
        sub="Six tiers, drawing from Robinhood Chain tokens plus BTC, SOL and tokenized gold and silver. Set the length on any of them; the price ladder is published below."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        {packs.map((pack) => (
          <PackCard key={pack.id} pack={pack} />
        ))}
      </div>

      <div className="mt-10">
        <SectionHeading
          title="The price ladder"
          sub="Every length on every tier. Adding a card costs more than the last one did, and buys a bigger stake on all of them."
        />
        <PriceLadder />
      </div>
    </div>
  )
}

function PriceLadder() {
  return (
    <div className="panel overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left" style={{ color: 'var(--color-faint)' }}>
              <th className="px-5 py-3 text-[0.6875rem] font-semibold tracking-wider uppercase">
                Cards
              </th>
              <th className="px-3 py-3 text-right text-[0.6875rem] font-semibold tracking-wider uppercase">
                Hits
              </th>
              {TIERS.map((tier) => (
                <th
                  key={tier}
                  className="px-3 py-3 text-right text-[0.6875rem] font-semibold tracking-wider uppercase"
                  style={{ color: TIER_ACCENT[tier] }}
                >
                  {TIER_LABEL[tier]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LENGTHS.map((cards) => (
              <tr key={cards} className="border-t">
                <td className="tnum px-5 py-2.5 font-semibold">{cards}</td>
                <td className="tnum px-3 py-2.5 text-right" style={{ color: 'var(--color-muted)' }}>
                  {hitsFor(cards)}
                </td>
                {TIERS.map((tier) => (
                  <td key={tier} className="px-3 py-2.5 text-right">
                    <Link
                      to={`/pack/${packId({ tier, cards })}`}
                      className="tnum font-medium hover:underline"
                    >
                      {usd(priceFor(tier, cards), { whole: true })}
                    </Link>
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t" style={{ background: 'var(--color-surface-2)' }}>
              <td
                className="px-5 py-2.5 text-[0.6875rem] font-semibold tracking-wider uppercase"
                colSpan={2}
                style={{ color: 'var(--color-faint)' }}
              >
                Published RTP
              </td>
              {TIERS.map((tier) => (
                <td
                  key={tier}
                  className="tnum px-3 py-2.5 text-right font-semibold"
                  style={{ color: TIER_ACCENT[tier] }}
                >
                  {percent(summaryFor({ tier, cards: MIN_CARDS }).rtp, 1)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <p
        className="border-t px-5 py-3 text-[0.6875rem] leading-relaxed"
        style={{ color: 'var(--color-faint)' }}
      >
        RTP is fixed per tier and does not move with length. A longer pack costs more per card
        because more money is riding on each of them. It returns the same share, with more variance.
      </p>
    </div>
  )
}
